import React, { useState } from 'react'
import ExtraPaymentModal from './ExtraPaymentModal'
import LoanDocuments from './LoanDocuments'
import { useCurrency } from '../context/CurrencyContext'
import { generateLoanCertificate } from '../utils/exportData'

function ord(n) {
  n = parseInt(n)
  const s = (n === 1 || n === 21 || n === 31) ? 'st' : (n === 2 || n === 22) ? 'nd' : (n === 3 || n === 23) ? 'rd' : 'th'
  return n + s
}

const typeLabel = { mortgage: 'Home Loan', auto: 'Auto Loan', student: 'Education Loan', personal: 'Personal Loan', credit: 'Credit Card' }
const typeColor = { mortgage: '#3d4a5c', auto: '#4a6741', student: '#b8441a', personal: '#7a5d1e', credit: '#c0392b' }
const typeBg = { mortgage: 'rgba(61,74,92,0.1)', auto: 'rgba(74,103,65,0.1)', student: 'rgba(184,68,26,0.08)', personal: 'rgba(201,168,76,0.12)', credit: 'rgba(192,57,43,0.08)' }
const fillColor = { mortgage: '#3d4a5c', auto: '#4a6741', student: '#b8441a', personal: '#c9a84c', credit: '#c0392b' }

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']

function fmtDate(d) {
  if (!d) return 'N/A'
  const parts = d.split('-')
  return MONTHS[parseInt(parts[1]) - 1] + ' ' + parts[0]
}

function calcMonthsLeft(balance, emi, annualRate) {
  if (!balance || !emi || emi <= 0) return null
  if (!annualRate) return Math.ceil(balance / emi)
  const r = annualRate / 12 / 100
  if (emi <= r * balance) return null
  return Math.ceil(-Math.log(1 - (r * balance) / emi) / Math.log(1 + r))
}

function debtFreeDate(balance, emi, annualRate) {
  const months = calcMonthsLeft(balance, emi, annualRate)
  if (!months || !isFinite(months)) return null
  const d = new Date()
  d.setMonth(d.getMonth() + months)
  return MONTHS[d.getMonth()] + ' ' + d.getFullYear()
}

function LoanDetailModal({ loan, emiPayments, onClose }) {
  const { fmt } = useCurrency()
  const balance = parseFloat(loan.balance)
  const original = parseFloat(loan.original_amount)
  const emi = parseFloat(loan.emi_amount)
  const extraEmi = parseFloat(loan.extra_emi_amount) || 0
  const effectiveEmi = emi + extraEmi
  const rate = parseFloat(loan.interest_rate)
  const pct = Math.min(100, Math.max(0, Math.round((1 - balance / original) * 100)))
  const monthsLeft = calcMonthsLeft(balance, effectiveEmi, rate)
  const interestRemaining = monthsLeft ? (effectiveEmi * monthsLeft) - balance : null
  const payoffDate = debtFreeDate(balance, effectiveEmi, rate)

  const now = new Date()
  const last6 = []
  for (let i = 5; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1)
    last6.push({ label: MONTHS[d.getMonth()], key: MONTHS[d.getMonth()] + ' ' + d.getFullYear() })
  }
  const payMap = {}
  emiPayments.forEach(p => { if (p.loan_id === loan.id) payMap[p.payment_month] = p.status })

  const barColor = { paid: '#4a6741', unpaid: '#b8441a', na: 'var(--text-muted)' }
  const barBg = { paid: 'rgba(74,103,65,0.12)', unpaid: 'rgba(184,68,26,0.1)', na: 'rgba(138,130,120,0.1)' }
  const barLabel = { paid: 'Paid', unpaid: 'Unpaid', na: 'N/A' }

  const row = (label, value, valueColor) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', padding: '0.6rem 0', borderBottom: '1px solid var(--surface-hdr)' }}>
      <span style={{ fontFamily: 'monospace', fontSize: '0.67rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-muted)' }}>{label}</span>
      <span style={{ fontFamily: 'Georgia,serif', fontSize: '0.9rem', fontWeight: '600', color: valueColor || 'var(--text)' }}>{value}</span>
    </div>
  )

  return (
    <div
      onClick={e => { if (e.target === e.currentTarget) onClose() }}
      style={{ position: 'fixed', inset: 0, background: 'var(--modal-backdrop)', backdropFilter: 'blur(4px)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}
    >
      <div style={{ background: 'var(--surface)', borderRadius: '10px', border: '1px solid var(--border)', width: '100%', maxWidth: '480px', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.15)' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', padding: '1.1rem 1.5rem', borderBottom: '1px solid var(--border)', background: 'var(--surface-hdr)' }}>
          <div>
            <div style={{ fontFamily: 'Georgia,serif', fontSize: '1.15rem', fontWeight: '700', marginBottom: '0.25rem' }}>{loan.name}</div>
            <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', alignItems: 'center' }}>
              <span style={{ display: 'inline-block', fontFamily: 'monospace', fontSize: '0.62rem', padding: '0.12rem 0.45rem', borderRadius: '3px', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '0.05em', background: typeBg[loan.type], color: typeColor[loan.type] }}>
                {typeLabel[loan.type] || loan.type}
              </span>
              {loan.family_member && (
                <span style={{ fontFamily: 'monospace', fontSize: '0.62rem', color: 'var(--text-muted)', padding: '0.12rem 0.45rem', borderRadius: '3px', background: 'var(--surface-hdr)', border: '1px solid var(--border)' }}>
                  👤 {loan.family_member}
                </span>
              )}
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '1.25rem', cursor: 'pointer', color: 'var(--text-muted)', lineHeight: 1, padding: '0.2rem', marginLeft: '1rem' }}>✕</button>
        </div>

        <div style={{ padding: '1.2rem 1.5rem', borderBottom: '1px solid var(--border)', background: 'var(--bg)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.45rem' }}>
            <span style={{ fontFamily: 'monospace', fontSize: '0.67rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-muted)' }}>Paid Off</span>
            <span style={{ fontFamily: 'monospace', fontSize: '0.75rem', fontWeight: '600', color: fillColor[loan.type] || '#c9a84c' }}>{pct}%</span>
          </div>
          <div style={{ height: '7px', background: 'var(--border)', borderRadius: '99px', overflow: 'hidden' }}>
            <div style={{ height: '100%', width: pct + '%', background: fillColor[loan.type] || '#c9a84c', borderRadius: '99px', transition: 'width 0.6s ease' }} />
          </div>
        </div>

        <div style={{ padding: '0.4rem 1.5rem 0.8rem' }}>
          {row('Current Balance', fmt(balance))}
          {row('Original Amount', fmt(original))}
          {row('Interest Rate', rate + '% p.a.')}
          {row('Monthly EMI', emi > 0 ? fmt(emi) + '/mo' : 'N/A')}
          {extraEmi > 0 && row('Recurring Extra', '+' + fmt(extraEmi) + '/mo', '#4a6741')}
          {row('EMI Due Day', loan.due_day ? ord(loan.due_day) + ' of every month' : 'N/A')}
          {row('Start Date', fmtDate(loan.start_date))}
          {row('End Date', fmtDate(loan.end_date))}
          {row('Months Remaining', monthsLeft != null ? monthsLeft + ' months' : 'N/A')}
          {payoffDate && row('Debt-Free Date', payoffDate, '#4a6741')}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', padding: '0.6rem 0' }}>
            <span style={{ fontFamily: 'monospace', fontSize: '0.67rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-muted)' }}>Total Interest Remaining</span>
            <span style={{ fontFamily: 'Georgia,serif', fontSize: '0.9rem', fontWeight: '600', color: interestRemaining != null && interestRemaining > 0 ? '#b8441a' : 'var(--text)' }}>
              {interestRemaining != null && interestRemaining > 0 ? fmt(interestRemaining) : 'N/A'}
            </span>
          </div>
        </div>

        <div style={{ padding: '0.9rem 1.5rem 1rem', borderTop: '1px solid var(--border)', background: 'var(--bg)' }}>
          <div style={{ fontFamily: 'monospace', fontSize: '0.67rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-muted)', marginBottom: '0.75rem' }}>
            EMI Payment History
          </div>
          <div style={{ display: 'flex', gap: '0.4rem' }}>
            {last6.map(({ label, key }) => {
              const status = payMap[key]
              const isToday = key === last6[5].key
              return (
                <div key={key} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.35rem' }}>
                  <div style={{ width: '100%', height: '36px', borderRadius: '4px', background: status ? barBg[status] : 'var(--surface-hdr)', border: `1.5px solid ${status ? barColor[status] : isToday ? '#c9a84c' : 'var(--border)'}`, borderStyle: status ? 'solid' : 'dashed', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {status === 'paid' && <span style={{ fontSize: '0.8rem', color: '#4a6741' }}>✓</span>}
                    {status === 'unpaid' && <span style={{ fontSize: '0.8rem', color: '#b8441a' }}>✗</span>}
                    {status === 'na' && <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)', fontFamily: 'monospace' }}>—</span>}
                    {!status && <span style={{ fontSize: '0.6rem', color: '#c9c3bb', fontFamily: 'monospace' }}>·</span>}
                  </div>
                  <span style={{ fontFamily: 'monospace', fontSize: '0.6rem', color: isToday ? 'var(--text)' : 'var(--text-muted)', fontWeight: isToday ? '600' : '400' }}>{label}</span>
                  {status && <span style={{ fontFamily: 'monospace', fontSize: '0.55rem', color: barColor[status], textTransform: 'uppercase', letterSpacing: '0.03em' }}>{barLabel[status]}</span>}
                </div>
              )
            })}
          </div>
        </div>

        <div style={{ padding: '0.8rem 1.5rem 1.2rem', borderTop: '1px solid var(--border)', display: 'flex', justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={{ background: 'var(--btn-bg)', color: 'var(--btn-text)', border: 'none', padding: '0.5rem 1.2rem', borderRadius: '4px', fontFamily: 'monospace', fontSize: '0.75rem', fontWeight: '500', cursor: 'pointer', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Close
          </button>
        </div>
      </div>
    </div>
  )
}

const TYPE_FILTERS = [
  { value: '', label: 'All' },
  { value: 'mortgage', label: 'Home Loan' },
  { value: 'auto', label: 'Auto Loan' },
  { value: 'student', label: 'Education Loan' },
  { value: 'personal', label: 'Personal Loan' },
  { value: 'credit', label: 'Credit Card' },
]

export default function LoanList({ loans, emiPayments, currentMonth, onEdit, onDelete, onSetEmi, loading, userId, userPhone, userName, onLoanUpdated, isPaid, onFeatureLock }) {
  const { fmt } = useCurrency()
  const [detailLoan, setDetailLoan] = useState(null)
  const [simulateLoan, setSimulateLoan] = useState(null)
  const [docsLoan, setDocsLoan] = useState(null)
  const [search, setSearch] = useState('')
  const [typeFilter, setTypeFilter] = useState('')

  const now = new Date()
  const CML = MONTHS[now.getMonth()] + ' ' + now.getFullYear()

  function getEmiStatus(loanId) {
    const p = emiPayments.find(p => p.loan_id === loanId && p.payment_month === currentMonth)
    return p ? p.status : null
  }

  function whatsappReminder(loan) {
    const emi = parseFloat(loan.emi_amount)
    const due = loan.due_day ? `on the ${ord(loan.due_day)}` : 'this month'
    const text = encodeURIComponent(`Hi! 👋 Reminder: Your EMI for *${loan.name}* of *${fmt(emi)}* is due ${due}.\n\nStay on track with DebtCrush! 💪`)
    const url = userPhone ? `https://wa.me/${userPhone.replace(/\D/g, '')}?text=${text}` : `https://wa.me/?text=${text}`
    window.open(url, '_blank')
  }

  if (loading) {
    return (
      <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '8px', padding: '3rem', textAlign: 'center', color: 'var(--text-muted)' }}>
        Loading your loans...
      </div>
    )
  }

  if (!loans.length) {
    return (
      <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '8px', padding: '4rem 2rem', textAlign: 'center' }}>
        <div style={{ fontSize: '2.5rem', marginBottom: '0.8rem' }}>📋</div>
        <div style={{ fontFamily: 'Georgia,serif', fontSize: '1.2rem', fontWeight: '700', marginBottom: '0.4rem' }}>No loans yet</div>
        <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>Add your first loan to start tracking</div>
      </div>
    )
  }

  const filtered = loans.filter(l => {
    const matchesSearch = l.name.toLowerCase().includes(search.toLowerCase()) ||
      (l.family_member && l.family_member.toLowerCase().includes(search.toLowerCase()))
    const matchesType = !typeFilter || l.type === typeFilter
    return matchesSearch && matchesType
  })

  return (
    <>
      {detailLoan && <LoanDetailModal loan={detailLoan} emiPayments={emiPayments} onClose={() => setDetailLoan(null)} />}
      {simulateLoan && <ExtraPaymentModal loan={simulateLoan} userId={userId} onClose={() => setSimulateLoan(null)} onUpdated={updated => { onLoanUpdated && onLoanUpdated(updated); setSimulateLoan(prev => prev ? { ...prev, ...updated } : prev) }} />}
      {docsLoan && userId && <LoanDocuments loan={docsLoan} userId={userId} onClose={() => setDocsLoan(null)} />}

      <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '8px', overflow: 'hidden' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '1rem 1.4rem', borderBottom: '1px solid var(--border)', background: 'var(--surface-hdr)' }}>
          <span style={{ fontFamily: 'Georgia,serif', fontSize: '1.05rem', fontWeight: '700' }}>Your Loans</span>
          <span style={{ fontFamily: 'monospace', fontSize: '0.7rem', color: 'var(--text-muted)' }}>
            {filtered.length !== loans.length ? `${filtered.length} of ${loans.length}` : loans.length} loan{loans.length !== 1 ? 's' : ''}
          </span>
        </div>

        <div style={{ display: 'flex', gap: '0.6rem', padding: '0.75rem 1.4rem', borderBottom: '1px solid var(--border)', background: 'var(--bg)', flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: '160px', position: 'relative' }}>
            <span style={{ position: 'absolute', left: '0.6rem', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)', fontSize: '0.75rem', pointerEvents: 'none' }}>⌕</span>
            <input
              type="text" placeholder="Search loans or family member..."
              value={search} onChange={e => setSearch(e.target.value)}
              style={{ width: '100%', boxSizing: 'border-box', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '4px', padding: '0.38rem 0.6rem 0.38rem 1.6rem', fontFamily: 'monospace', fontSize: '0.75rem', color: 'var(--text)', outline: 'none' }}
              onFocus={e => e.target.style.borderColor = 'var(--text)'}
              onBlur={e => e.target.style.borderColor = 'var(--border)'}
            />
          </div>
          <div style={{ display: 'flex', gap: '0.3rem', flexWrap: 'wrap' }}>
            {TYPE_FILTERS.map(f => (
              <button key={f.value} onClick={() => setTypeFilter(f.value)}
                style={{ padding: '0.32rem 0.65rem', borderRadius: '20px', border: '1.5px solid', borderColor: typeFilter === f.value ? 'var(--btn-bg)' : 'var(--border)', background: typeFilter === f.value ? 'var(--btn-bg)' : 'transparent', color: typeFilter === f.value ? 'var(--btn-text)' : 'var(--text-muted)', fontFamily: 'monospace', fontSize: '0.63rem', fontWeight: '500', cursor: 'pointer', whiteSpace: 'nowrap', transition: 'all 0.15s' }}>
                {f.label}
              </button>
            ))}
          </div>
        </div>

        {filtered.length === 0 && (
          <div style={{ padding: '3rem 2rem', textAlign: 'center', color: 'var(--text-muted)', fontFamily: 'monospace', fontSize: '0.8rem' }}>
            No loans match your search.
          </div>
        )}

        {filtered.map((loan, i) => {
          const balance = parseFloat(loan.balance)
          const original = parseFloat(loan.original_amount)
          const emi = parseFloat(loan.emi_amount)
          const extraEmi = parseFloat(loan.extra_emi_amount) || 0
          const effectiveEmi = emi + extraEmi
          const rate = parseFloat(loan.interest_rate)
          const pct = Math.min(100, Math.max(0, Math.round((1 - balance / original) * 100)))
          const monthsLeft = calcMonthsLeft(balance, effectiveEmi, rate)
          const payoffDate = debtFreeDate(balance, effectiveEmi, rate)
          const st = getEmiStatus(loan.id)

          return (
            <div key={loan.id} style={{ padding: '1.1rem 1.4rem', borderBottom: i < filtered.length - 1 ? '1px solid var(--border)' : 'none', transition: 'background 0.15s' }}
              onMouseEnter={e => e.currentTarget.style.background = 'rgba(201,168,76,0.04)'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.6rem' }}>
                <div>
                  <div style={{ fontWeight: '500', fontSize: '0.92rem', marginBottom: '0.2rem' }}>{loan.name}</div>
                  <div style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap', alignItems: 'center' }}>
                    <span style={{ display: 'inline-block', fontFamily: 'monospace', fontSize: '0.62rem', padding: '0.12rem 0.45rem', borderRadius: '3px', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '0.05em', background: typeBg[loan.type], color: typeColor[loan.type] }}>
                      {typeLabel[loan.type] || loan.type}
                    </span>
                    {loan.family_member && (
                      <span style={{ fontFamily: 'monospace', fontSize: '0.6rem', color: 'var(--text-muted)', padding: '0.1rem 0.4rem', borderRadius: '3px', background: 'var(--surface-hdr)', border: '1px solid var(--border)' }}>
                        👤 {loan.family_member}
                      </span>
                    )}
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontFamily: 'Georgia,serif', fontSize: '1.2rem', fontWeight: '700' }}>{fmt(balance)}</div>
                  <div style={{ fontFamily: 'monospace', fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: '0.1rem' }}>{loan.interest_rate}% p.a.</div>
                </div>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '0.5rem' }}>
                <div style={{ flex: 1, height: '5px', background: 'var(--border)', borderRadius: '99px', overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: pct + '%', background: fillColor[loan.type] || '#c9a84c', borderRadius: '99px', transition: 'width 0.6s ease' }} />
                </div>
                <span style={{ fontFamily: 'monospace', fontSize: '0.68rem', color: 'var(--text-muted)', minWidth: '30px', textAlign: 'right' }}>{pct}%</span>
              </div>

              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.8rem', fontFamily: 'monospace', fontSize: '0.72rem', color: 'var(--text-muted)', marginBottom: '0.75rem' }}>
                <span>EMI: {fmt(emi)}/mo{extraEmi > 0 && <span style={{ color: '#4a6741' }}> +{fmt(extraEmi)}</span>}</span>
                <span>Due: {loan.due_day ? ord(loan.due_day) + ' every mo' : 'N/A'}</span>
                {monthsLeft != null && <span>~{monthsLeft} mo left</span>}
                {payoffDate && <span style={{ color: '#4a6741', fontWeight: '600' }}>🏁 {payoffDate}</span>}
                {st === 'paid' && <span style={{ color: '#4a6741', fontWeight: '600' }}>✓ Paid</span>}
                {st === 'unpaid' && <span style={{ color: '#b8441a', fontWeight: '600' }}>✗ Unpaid</span>}
                {st === 'na' && <span style={{ color: '#3d4a5c' }}>— N/A</span>}
              </div>

              <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '0.4rem', paddingTop: '0.75rem', borderTop: '1px dashed var(--border)', marginBottom: '0.6rem' }}>
                <span style={{ fontFamily: 'monospace', fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-muted)' }}>
                  EMI {CML}:
                </span>
                {['paid', 'unpaid', 'na'].map(s => (
                  <button key={s} onClick={() => onSetEmi(loan.id, s)}
                    style={{ padding: '0.28rem 0.7rem', borderRadius: '20px', border: '1.5px solid', borderColor: st === s ? (s === 'paid' ? '#4a6741' : s === 'unpaid' ? '#b8441a' : '#3d4a5c') : 'var(--border)', fontFamily: 'monospace', fontSize: '0.65rem', fontWeight: '500', cursor: 'pointer', background: st === s ? (s === 'paid' ? '#4a6741' : s === 'unpaid' ? '#b8441a' : '#3d4a5c') : 'transparent', color: st === s ? '#fff' : 'var(--text-muted)', transition: 'all 0.15s' }}>
                    {s === 'paid' ? '✓ Paid' : s === 'unpaid' ? '✗ Not Paid' : '— N/A'}
                  </button>
                ))}
                <span style={{ marginLeft: 'auto', fontFamily: 'monospace', fontSize: '0.63rem', color: 'var(--text-muted)', background: 'var(--surface-hdr)', padding: '0.18rem 0.45rem', borderRadius: '3px' }}>
                  {CML}
                </span>
              </div>

              {/* Primary actions row */}
              <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', marginBottom: '0.35rem' }}>
                <button onClick={() => setDetailLoan(loan)}
                  style={{ background: 'none', border: '1px solid var(--border)', padding: '0.22rem 0.6rem', borderRadius: '3px', fontSize: '0.68rem', fontFamily: 'monospace', cursor: 'pointer', color: 'var(--text-muted)', transition: 'all 0.15s' }}
                  onMouseEnter={e => { e.target.style.borderColor = '#3d4a5c'; e.target.style.color = '#3d4a5c' }}
                  onMouseLeave={e => { e.target.style.borderColor = 'var(--border)'; e.target.style.color = 'var(--text-muted)' }}>
                  Detail
                </button>
                <button onClick={() => onEdit(loan)}
                  style={{ background: 'none', border: '1px solid var(--border)', padding: '0.22rem 0.6rem', borderRadius: '3px', fontSize: '0.68rem', fontFamily: 'monospace', cursor: 'pointer', color: 'var(--text-muted)', transition: 'all 0.15s' }}
                  onMouseEnter={e => { e.target.style.borderColor = 'var(--text)'; e.target.style.color = 'var(--text)' }}
                  onMouseLeave={e => { e.target.style.borderColor = 'var(--border)'; e.target.style.color = 'var(--text-muted)' }}>
                  Edit
                </button>
                <button onClick={() => {
                    if (!isPaid) { onFeatureLock?.('Extra Payment Simulator', 'Simulate the impact of extra payments, set up recurring top-ups, and see exactly how many months and how much interest you save — with DebtCrush Premium (₹149/month).'); return }
                    setSimulateLoan(loan)
                  }}
                  style={{ background: isPaid ? 'rgba(201,168,76,0.08)' : 'rgba(138,130,120,0.08)', border: `1px solid ${isPaid ? '#c9a84c' : 'var(--border)'}`, padding: '0.22rem 0.7rem', borderRadius: '3px', fontSize: '0.68rem', fontFamily: 'monospace', cursor: 'pointer', color: isPaid ? '#c9a84c' : 'var(--text-muted)', fontWeight: '600', transition: 'all 0.15s' }}
                  onMouseEnter={e => { e.currentTarget.style.background = isPaid ? '#c9a84c' : 'rgba(138,130,120,0.15)'; e.currentTarget.style.color = isPaid ? '#0d0d0d' : 'var(--text-muted)' }}
                  onMouseLeave={e => { e.currentTarget.style.background = isPaid ? 'rgba(201,168,76,0.08)' : 'rgba(138,130,120,0.08)'; e.currentTarget.style.color = isPaid ? '#c9a84c' : 'var(--text-muted)' }}>
                  {isPaid ? '💡 Extra Payment' : '🔒 Extra Payment'}
                </button>
                <button onClick={() => onDelete(loan.id)}
                  style={{ background: 'none', border: '1px solid var(--border)', padding: '0.22rem 0.6rem', borderRadius: '3px', fontSize: '0.68rem', fontFamily: 'monospace', cursor: 'pointer', color: 'var(--text-muted)', transition: 'all 0.15s' }}
                  onMouseEnter={e => { e.target.style.borderColor = '#c0392b'; e.target.style.color = '#c0392b' }}
                  onMouseLeave={e => { e.target.style.borderColor = 'var(--border)'; e.target.style.color = 'var(--text-muted)' }}>
                  Remove
                </button>
              </div>
              {/* Secondary actions row */}
              <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                <button onClick={() => setDocsLoan(loan)}
                  style={{ background: 'none', border: '1px solid var(--border)', padding: '0.22rem 0.6rem', borderRadius: '3px', fontSize: '0.68rem', fontFamily: 'monospace', cursor: 'pointer', color: 'var(--text-muted)', transition: 'all 0.15s' }}
                  onMouseEnter={e => { e.target.style.borderColor = '#3d4a5c'; e.target.style.color = '#3d4a5c' }}
                  onMouseLeave={e => { e.target.style.borderColor = 'var(--border)'; e.target.style.color = 'var(--text-muted)' }}>
                  📎 Docs
                </button>
                <button onClick={() => generateLoanCertificate(loan, emiPayments.filter(p => p.loan_id === loan.id), userName)}
                  style={{ background: 'none', border: '1px solid var(--border)', padding: '0.22rem 0.6rem', borderRadius: '3px', fontSize: '0.68rem', fontFamily: 'monospace', cursor: 'pointer', color: 'var(--text-muted)', transition: 'all 0.15s' }}
                  onMouseEnter={e => { e.target.style.borderColor = '#c9a84c'; e.target.style.color = '#c9a84c' }}
                  onMouseLeave={e => { e.target.style.borderColor = 'var(--border)'; e.target.style.color = 'var(--text-muted)' }}>
                  🧾 Certificate
                </button>
                <button onClick={() => whatsappReminder(loan)}
                  style={{ background: 'none', border: '1px solid var(--border)', padding: '0.22rem 0.6rem', borderRadius: '3px', fontSize: '0.68rem', fontFamily: 'monospace', cursor: 'pointer', color: 'var(--text-muted)', transition: 'all 0.15s' }}
                  onMouseEnter={e => { e.target.style.borderColor = '#4a6741'; e.target.style.color = '#4a6741' }}
                  onMouseLeave={e => { e.target.style.borderColor = 'var(--border)'; e.target.style.color = 'var(--text-muted)' }}>
                  💬 WhatsApp
                </button>
              </div>
            </div>
          )
        })}
      </div>
    </>
  )
}
